import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types';
import { X, Wallet } from 'lucide-react';
interface MoneyAssemblerProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
export function MoneyAssembler({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: MoneyAssemblerProps) {
  // Parse question data
  const targetAmount = question.correctAnswer; // in cents
  const availableDenominations = question.options.map(Number);
  // State
  const [selectedCoins, setSelectedCoins] = useState<number[]>([]);
  const totalAmount = selectedCoins.reduce((sum, coin) => sum + coin, 0);
  // Check if anything is selected
  const isReady = selectedCoins.length > 0;
  // Notify parent when ready state changes
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  // Handle validation trigger
  useEffect(() => {
    if (shouldValidate) {
      const userAnswer = selectedCoins.join(',');
      onComplete(userAnswer);
    }
  }, [shouldValidate, selectedCoins, onComplete]);
  // Add coin
  const handleAddCoin = (denomination: number) => {
    setSelectedCoins([...selectedCoins, denomination]);
  };
  // Remove coin
  const handleRemoveCoin = (index: number) => {
    setSelectedCoins(selectedCoins.filter((_, i) => i !== index));
  };
  // Format money display
  const formatMoney = (cents: number): string => {
    const euros = Math.floor(cents / 100);
    const remainingCents = cents % 100;
    if (remainingCents === 0) return `€${euros},-`;
    return `€${euros},${remainingCents.toString().padStart(2, '0')}`;
  };
  // Get denomination display text
  const getDenominationDisplay = (cents: number): string => {
    if (cents >= 100) return `€${cents / 100}`;
    return `${cents}`; // Just the number for cents, "cent" text can be small below or omitted for cleaner look
  };
  // Helper to determine if it's a bill or coin
  const isBill = (cents: number) => cents >= 500;
  // Get visual styles for specific denominations
  const getMoneyStyles = (cents: number) => {
    // BILLS
    if (cents === 5000) {
      // €50
      return {
        container: 'w-28 h-16 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 border-orange-700',
        text: 'text-white font-bold text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 2000) {
      // €20
      return {
        container: 'w-26 h-15 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 border-blue-700',
        text: 'text-white font-bold text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 1000) {
      // €10
      return {
        container: 'w-24 h-14 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-rose-400 via-rose-500 to-rose-600 border-rose-700',
        text: 'text-white font-bold text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 500) {
      // €5
      return {
        container: 'w-22 h-13 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-slate-400 via-slate-500 to-slate-600 border-slate-700',
        text: 'text-white font-bold text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    // COINS
    if (cents === 200) {
      // €2
      return {
        container: 'w-16 h-16 rounded-full shadow-lg',
        bg: 'bg-gradient-to-br from-slate-300 via-slate-100 to-slate-400 border-slate-400',
        text: 'text-slate-800 font-bold text-lg',
        border: 'border-2',
        inner: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-500 rounded-full w-10 h-10 flex items-center justify-center shadow-inner border border-amber-600/30'
      };
    }
    if (cents === 100) {
      // €1
      return {
        container: 'w-14 h-14 rounded-full shadow-lg',
        bg: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-500 border-amber-600',
        text: 'text-amber-900 font-bold text-lg',
        border: 'border-2',
        inner: 'bg-gradient-to-br from-slate-300 via-slate-100 to-slate-400 rounded-full w-9 h-9 flex items-center justify-center shadow-inner border border-slate-400/30'
        // Note: Real €1 has silver inside, gold outside. Wait, actually €1 is silver inside, gold outside. €2 is gold inside, silver outside.
        // Let's correct:
        // €1: Silver center, Gold ring.
        // €2: Gold center, Silver ring.
        // My code above for €2 has silver outside (bg) and gold inside (inner). That is correct.
        // For €1, it should be Gold outside (bg) and Silver inside (inner).
      };
    }
    if (cents === 50) {
      return {
        container: 'w-14 h-14 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-bold text-lg',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center' // No inner ring
      };
    }
    if (cents === 20) {
      return {
        container: 'w-12 h-12 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-bold text-base',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center',
        notch: true // Visual trick for "Spanish flower" shape? Maybe just circle for now.
      };
    }
    if (cents === 10) {
      return {
        container: 'w-10 h-10 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-bold text-sm',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents <= 5) {
      return {
        container: 'w-9 h-9 rounded-full shadow-sm',
        bg: 'bg-gradient-to-br from-orange-300 via-orange-200 to-orange-500 border-orange-600',
        text: 'text-orange-900 font-bold text-xs',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    // Fallback
    return {
      container: 'w-12 h-12 rounded-full',
      bg: 'bg-gray-200',
      text: 'text-gray-800',
      border: 'border',
      inner: ''
    };
  };
  // Render a single money item
  const MoneyItem = ({
    cents,
    onClick,
    disabled = false,
    small = false
  }: {
    cents: number;
    onClick?: () => void;
    disabled?: boolean;
    small?: boolean;
  }) => {
    const styles = getMoneyStyles(cents);
    const isBillItem = isBill(cents);
    // Scale down for "small" view (in wallet)
    const scaleClass = small ? 'scale-75 origin-center' : '';
    return <motion.button whileHover={!disabled && !small ? {
      scale: 1.1,
      rotate: 2,
      y: -2
    } : {}} whileTap={!disabled && !small ? {
      scale: 0.95
    } : {}} onClick={onClick} disabled={disabled} className={`
          relative flex items-center justify-center
          ${styles.container} ${styles.bg} ${styles.border}
          ${scaleClass}
          transition-shadow duration-200
          ${!disabled && !small ? 'hover:shadow-xl cursor-pointer' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `} aria-label={getDenominationDisplay(cents)} data-id="element-1062">
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/0 rounded-[inherit] pointer-events-none" data-id="element-1063" />

        {/* Inner content */}
        <div className={`${styles.inner} relative z-10`} data-id="element-1064">
          <span className={`${styles.text} drop-shadow-sm`} data-id="element-1065">
            {getDenominationDisplay(cents)}
            {cents < 100 && <span className="text-[0.6em] ml-0.5" data-id="element-1066">c</span>}
          </span>
        </div>

        {/* Bill specific details */}
        {isBillItem && <>
            <div className="absolute top-1 left-2 text-[0.6rem] text-white/80 font-mono" data-id="element-1067">
              EURO
            </div>
            <div className="absolute bottom-1 right-2 text-[0.6rem] text-white/80 font-mono" data-id="element-1068">
              EYPO
            </div>
            {/* Hologram strip simulation */}
            <div className="absolute right-4 top-0 bottom-0 w-2 bg-white/10 border-l border-r border-white/20" data-id="element-1069" />
          </>}
      </motion.button>;
  };
  return <div className="w-full max-w-3xl mx-auto space-y-8" data-id="element-1070">
      {/* Target Amount Display */}
      <div className="relative" data-id="element-1071">
        <div className="absolute inset-0 bg-indigo-500 blur-2xl opacity-10 rounded-full" data-id="element-1072" />
        <div className="bg-white rounded-2xl p-6 border-2 border-indigo-100 shadow-lg text-center relative z-10" data-id="element-1073">
          <p className="text-sm font-bold text-indigo-500 mb-2 uppercase tracking-wider" data-id="element-1074">
            Doelbedrag
          </p>
          <motion.div key={targetAmount} initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="text-5xl md:text-6xl font-black text-indigo-900 tracking-tight" data-id="element-1075">
            {formatMoney(targetAmount)}
          </motion.div>
        </div>
      </div>

      {/* Available Money Grid */}
      <div className="space-y-4" data-id="element-1076">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-1077">
          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs" data-id="element-1078">
            1
          </span>
          Kies munten en biljetten
        </h3>

        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 shadow-inner" data-id="element-1079">
          <div className="flex flex-wrap gap-4 justify-center items-center" data-id="element-1080">
            {availableDenominations.map((denomination, index) => <motion.div key={`denom-${denomination}`} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} data-id="element-1081">
                <MoneyItem cents={denomination} onClick={() => handleAddCoin(denomination)} data-id="element-1082" />
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Wallet / Selection Area */}
      <div className="space-y-4" data-id="element-1083">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-1084">
          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs" data-id="element-1085">
            2
          </span>
          Jouw portemonnee
        </h3>

        <div className="relative min-h-[180px] bg-gradient-to-b from-slate-100 to-slate-200 rounded-2xl border-2 border-slate-300 overflow-hidden group" data-id="element-1086">
          {/* Wallet Texture/Pattern */}
          <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-1087" />

          {/* Empty State */}
          {selectedCoins.length === 0 && <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none" data-id="element-1088">
              <Wallet className="w-12 h-12 mb-2 opacity-50" data-id="element-1089" />
              <p className="font-medium" data-id="element-1090">Je portemonnee is leeg</p>
              <p className="text-xs opacity-75" data-id="element-1091">Klik op het geld hierboven</p>
            </div>}

          {/* Selected Items Grid */}
          <div className="relative z-10 p-6 flex flex-wrap content-start gap-3" data-id="element-1092">
            <AnimatePresence mode="popLayout" data-id="element-1093">
              {selectedCoins.map((coin, index) => <motion.div key={`selected-${index}-${coin}`} layout initial={{
              scale: 0,
              opacity: 0,
              y: 50
            }} animate={{
              scale: 1,
              opacity: 1,
              y: 0
            }} exit={{
              scale: 0,
              opacity: 0,
              transition: {
                duration: 0.2
              }
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25
            }} className="relative group/coin" data-id="element-1094">
                  <MoneyItem cents={coin} small data-id="element-1095" />

                  {/* Remove Button Overlay */}
                  <button onClick={() => handleRemoveCoin(index)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover/coin:opacity-100 transition-opacity hover:bg-red-600 hover:scale-110" aria-label="Verwijder munt" data-id="element-1096">
                    <X className="w-3 h-3" data-id="element-1097" />
                  </button>
                </motion.div>)}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}